/**
 * Created by christiancueni on 26/09/16.
 * Copyright (c) 2016 ITerativ GmbH. All rights reserved.
 */

import { Injectable } from '@angular/core';

import * as dayjs from 'dayjs';
import { Weekday } from '../models';

@Injectable()
export class CalendarService {

    constructor() {
    }

    private getWeekdays(weekday:dayjs.Dayjs): Weekday[] {
        let weekdays = [];

        for (let i = 1; i < 6; i++) {
            weekdays.push(new Weekday(dayjs(weekday).day(i)));
        }
        return weekdays;
    }

    private getWeekdaysOnly(dates: dayjs.Dayjs[]): dayjs.Dayjs[] {
        let weekdays = [];

        for ( let i = 0; i < dates.length;i++) {
            if (dates[i].day() !== 0 && dates[i].day() !== 6) {
                weekdays.push(dates[i]);
            }
        }
        return weekdays;
    }

    getFirstMondayOfMonth(month, year) {
        let firstOfMonth = dayjs(`${year}-${month}-01`);
        let dayOfWeek = firstOfMonth.isoWeekday();

        if (dayOfWeek === 1) {
            return firstOfMonth;
        } else {
            return firstOfMonth.add(8 - dayOfWeek, 'days');
        }
    }

    getCurrentWeekDays(): dayjs.Dayjs[] {
        return this.weekdaysToMoment(this.getWeekdays(dayjs(new Date() )));
    }

    getToday(): dayjs.Dayjs {
        return dayjs(new Date());
    }

    getWeekdaysForDate(stringDate: string): string[] {
        let date = dayjs(stringDate);
        return this.weekdaysToString(this.getWeekdays(date));
    }

    private weekdaysToMoment(weekdays: Weekday[]) {
        return weekdays.map(weekday => weekday.weekdayAsDayjs());
    }

    private weekdaysToString(dates: Weekday[]): string[] {
        return dates.map(date => date.weekdayAsString());
    }

    getPrecedingMondayForDate(date: dayjs.Dayjs): dayjs.Dayjs {
        return dayjs(date).startOf('isoWeek');
    }

    getNextWeek(date): dayjs.Dayjs[] {
        const monday = this.getPrecedingMondayForDate(date);
        return this.weekdaysToMoment(this.getWeekdays(monday.add(7, 'days')));
    }

    getPreviousWeek(day): dayjs.Dayjs[] {
        return this.weekdaysToMoment(this.getWeekdays(day.subtract(7, 'days')));
    }

    getFirstWeekOfMonth(month, year) {
        let firstMonday = this.getFirstMondayOfMonth(month, year);
        return this.getWeekdays(firstMonday);
    }

    getDateRange(start: dayjs.Dayjs, end: dayjs.Dayjs): dayjs.Dayjs[] {
        let dates = [];
        let rangeLength = end.diff(start, 'days');
        for (let i = 0; i < rangeLength; i++) {
            dates.push(dayjs(start).add(i, 'days'));
        }
        return dates;
    }

    getStartAndEndForNextTwoWeeks(): Object[] {
        const nextWeek = this.getNextWeek(this.getToday());
        const weekAfterNextWeek = this.getNextWeek(this.getNextWeek(this.getToday())[0]);
        return [
            {start: Weekday.dayjsAsString(nextWeek[0]), end: Weekday.dayjsAsString(nextWeek[4])},
            {start: Weekday.dayjsAsString(weekAfterNextWeek[0]), end: Weekday.dayjsAsString(weekAfterNextWeek[4])}
        ];
    }

    // check if still used
    getDateRangeAroundDate(date: Date, delta: number): dayjs.Dayjs[] {
        let startDate = dayjs(date).subtract(delta, 'days');
        let endDate = dayjs(date).add(delta, 'days');

        return this.getWeekdaysOnly(this.getDateRange(startDate, endDate));
    }

    getWeekendsForYear(year: number) {
        let startDate = new Date(year, 0, 1),
            endDate = new Date(year, 11, 31);

        return this.getDateRange(dayjs(startDate), dayjs(endDate))
            .filter(date => date.day() === 0 || date.day() === 6);
    }
}
